import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { interviewService } from '@/services/api';

// Types
interface Question {
  uid: string;
  text: string;
  type: 'technical' | 'behavioral' | 'situational';
  difficulty: 'easy' | 'medium' | 'hard';
  timeLimit: number; // seconds
  order: number;
}

interface Answer {
  questionUid: string;
  answer: string;
  audioUrl?: string;
  videoUrl?: string;
  duration: number;
  timestamp: string;
}

export interface InterviewResult {
  uid: string;
  candidateName: string;
  candidateEmail: string;
  interviewDate: string;
  duration: number;
  overallScore: number;
  technicalScore: number;
  communicationScore: number;
  behavioralScore: number;
  questions: {
    question: string;
    answer: string;
    score: number;
    feedback: string;
  }[];
  aiAnalysis?: string;
  status: 'pending' | 'completed' | 'reviewed';
}

type InterviewStatus =
  | 'idle'
  | 'preparing'
  | 'in_progress'
  | 'paused'
  | 'completed'
  | 'error';

interface InterviewState {
  currentInterview: {
    uid: string | null;
    status: InterviewStatus;
    startTime: string | null;
    endTime: string | null;
    currentQuestionIndex: number;
    questions: Question[];
    answers: Answer[];
  };
  result: InterviewResult | null;
  allResults: InterviewResult[];
  mediaStream: {
    hasCamera: boolean;
    hasMicrophone: boolean;
    isCameraOn: boolean;
    isMicrophoneOn: boolean;
  };
  webSocket: {
    isConnected: boolean;
    connectionError: string | null;
  };
  isLoading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
}

// Initial state
const initialState: InterviewState = {
  currentInterview: {
    uid: null,
    status: 'idle',
    startTime: null,
    endTime: null,
    currentQuestionIndex: 0,
    questions: [],
    answers: [],
  },
  result: null,
  allResults: [],
  mediaStream: {
    hasCamera: false,
    hasMicrophone: false,
    isCameraOn: false,
    isMicrophoneOn: false,
  },
  webSocket: {
    isConnected: false,
    connectionError: null,
  },
  isLoading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
  },
};

export const fetchInterviewResult = createAsyncThunk(
  'interview/fetchResult',
  async (interviewUid: string, { rejectWithValue }) => {
    try {
      const response = await interviewService.getInterviewResult(interviewUid);
      return response;
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to fetch interview result';
      return rejectWithValue(message);
    }
  }
);

export const fetchAllResults = createAsyncThunk(
  'interview/fetchAllResults',
  async (
    params: { page?: number; limit?: number; status?: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await interviewService.getAllResults(params);
      return response;
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to fetch results';
      return rejectWithValue(message);
    }
  }
);

// Slice
const interviewSlice = createSlice({
  name: 'interview',
  initialState,
  reducers: {
    setPagination: (
      state,
      action: PayloadAction<{ page?: number; limit?: number }>
    ) => {
      if (action.payload.page !== undefined) {
        state.pagination.page = action.payload.page;
      }
      if (action.payload.limit !== undefined) {
        state.pagination.limit = action.payload.limit;
      }
    },
  },
  extraReducers: builder => {
    // Fetch Interview Result
    builder
      .addCase(fetchInterviewResult.pending, state => {
        state.isLoading = true;
      })
      .addCase(fetchInterviewResult.fulfilled, (state, action) => {
        state.isLoading = false;
        state.result = action.payload;
      })
      .addCase(fetchInterviewResult.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch All Results
    builder
      .addCase(fetchAllResults.pending, state => {
        state.isLoading = true;
      })
      .addCase(fetchAllResults.fulfilled, (state, action) => {
        state.isLoading = false;
        state.allResults = action.payload.results;
        state.pagination.total = action.payload.total;
      })
      .addCase(fetchAllResults.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setPagination } = interviewSlice.actions;

export default interviewSlice.reducer;
